
import { type CollisionMap, FLAG_BLOCKED, FLAG_WATER } from "../sim/collision";
import { Camera } from "./camera";
import type { EffectRenderer } from "./effects";
import { EntityPainter, type RenderFrame, type RenderedCharacterParts } from "./entities";
import type { ClipEntry, ClipName } from "./creaturemesh";
import type { PlacedSphere } from "./collisionvolumes";
import type { CreaturePalette } from "./creature";
import type { HeightField } from "./heightfield";
import type { RenderBackend, SceneRenderer } from "./scene";
import { Snowfall } from "./snow";
import { waterSurfaceColour } from "./water";
import { clamp } from "../clamp";

const COLOURS = {
  void: "#b0bdd6",
  grid: "rgba(255, 255, 255, 0.04)",
} as const;

export type { RenderFrame };

export class Renderer implements SceneRenderer {
  readonly backend: RenderBackend = "canvas2d";
  readonly camera = new Camera();
  private readonly ctx: CanvasRenderingContext2D;
  private readonly painter: EntityPainter;
  private terrain: HTMLCanvasElement | null = null;
  private terrainMap: CollisionMap | null = null;
  private dpr = 1;
  private pixelRatioCap = 2;
  private night = false;
  private snow: Snowfall | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly heightField: HeightField,
  ) {
    const ctx = canvas.getContext("2d", { alpha: false });
    if (ctx === null) throw new Error("Canvas2D is unavailable in this browser");
    this.ctx = ctx;
    this.painter = new EntityPainter(ctx, this.camera, heightField);
    this.resize();
  }

  setPalettes(palettes: Readonly<Record<string, CreaturePalette>>): void {
    this.painter.setPalettes(palettes);
  }

  setTerrain(map: CollisionMap): void {
    const cache = document.createElement("canvas");
    cache.width = map.worldCellsX;
    cache.height = map.worldCellsY;
    const ctx = cache.getContext("2d");
    if (ctx === null) throw new Error("Canvas2D is unavailable in this browser");

    const image = ctx.createImageData(map.worldCellsX, map.worldCellsY);
    const pixels = image.data;
    const cells = map.cells;

    for (let i = 0; i < cells.length; i++) {
      const flags = cells[i];
      // The cell grid's y axis runs north-up like the world; the image's runs
      // down. Flipping here keeps every draw call afterwards a plain blit.
      const cx = i % map.worldCellsX;
      const cy = map.worldCellsY - 1 - Math.floor(i / map.worldCellsX);
      const at = (cy * map.worldCellsX + cx) * 4;

      let rgb: readonly [number, number, number];
      if (flags & FLAG_BLOCKED) rgb = [21, 24, 29];
      else if (flags & FLAG_WATER) rgb = [29, 63, 102];
      // A faint checker on walkable ground: at this scale a flat fill gives the
      // eye nothing to judge motion against, and the player feels stationary.
      else rgb = (cx + cy) % 2 === 0 ? [47, 74, 51] : [51, 81, 58];

      pixels[at] = rgb[0];
      pixels[at + 1] = rgb[1];
      pixels[at + 2] = rgb[2];
      pixels[at + 3] = 255;
    }
    ctx.putImageData(image, 0, 0);

    this.terrain = cache;
    this.terrainMap = map;
  }

  groundAtMm(xMm: number, yMm: number): number {
    return this.heightField.at(xMm, yMm);
  }

  weaponAttackClip(vnum: number): ClipName {
    return this.painter.weaponAttackClip(vnum);
  }

  activeClip(entityId: number): ClipName | null {
    return this.painter.activeClip(entityId);
  }

  activeClipEntry(entityId: number): ClipEntry | null {
    return this.painter.activeClipEntry(entityId);
  }

  attackClipEntry(armourVnum: number, race: number, clip: ClipName): ClipEntry | null {
    return this.painter.attackClipEntry(armourVnum, race, clip);
  }

  defendingSpheres(entityId: number): readonly PlacedSphere[] {
    return this.painter.defendingSpheres(entityId);
  }

  attackClipDurationMs(armourVnum: number, race: number, clip: ClipName): number {
    return this.painter.attackClipDurationMs(armourVnum, race, clip);
  }

  attackHitStartMs(armourVnum: number, race: number, clip: ClipName): number | null {
    return this.painter.attackHitStartMs(armourVnum, race, clip);
  }

  skillClipHoldMs(armourVnum: number, race: number, clip: ClipName, mountedWeapon: number | null = null): number | null {
    return this.painter.skillClipHoldMs(armourVnum, race, clip, mountedWeapon);
  }

  attackIsRanged(weaponVnum: number): boolean {
    return this.painter.attackIsRanged(weaponVnum);
  }

  comboChainGapMsFor(armourVnum: number, race: number, clip: ClipName): number | null {
    return this.painter.comboChainGapMsFor(armourVnum, race, clip);
  }

  comboChainFor(race: number, clip: ClipName, comboType: number): readonly ClipName[] | null {
    return this.painter.comboChainFor(race, clip, comboType);
  }

  comboSwingIntervalMsFor(
    armourVnum: number, race: number, clip: ClipName, attackSpeed: number,
  ): number | null {
    return this.painter.comboSwingIntervalMsFor(armourVnum, race, clip, attackSpeed);
  }

  /** Present but inert: this renderer's canvas is opaque and there is nowhere
   *  in its single interleaved pass for a transparent particle layer to sit.
   *  See `effects.ts`. */
  get effects(): EffectRenderer {
    return this.painter.effects;
  }

  /** The mesh character assembly from the latest frame.  Kept on both
   * renderer backends so a browser regression can ask what was actually drawn
   * without depending on whether the machine chose WebGL or Canvas2D. */
  renderedCharacterParts(entityId: number): RenderedCharacterParts | null {
    return this.painter.renderedCharacterParts(entityId);
  }

  /** See `SceneRenderer.weaponAnchor`. */
  weaponAnchor(entityId: number): { xMm: number; yMm: number; zMm: number; headingRad: number } | null {
    return this.painter.weaponAnchor(entityId);
  }

  /** See `SceneRenderer.weaponAnchorLeft`. */
  weaponAnchorLeft(entityId: number): { xMm: number; yMm: number; zMm: number; headingRad: number } | null {
    return this.painter.weaponAnchorLeft(entityId);
  }

  /** See `SceneRenderer.headAnchor`. */
  headAnchor(entityId: number, mountVnum = 0): { xMm: number; yMm: number; zMm: number } | null {
    return this.painter.headAnchor(entityId, mountVnum);
  }

  /** Where the GM mark was actually painted for this entity, in CSS px, on
   *  the last frame that drew one. See `EntityPainter.markRect`. */
  markRect(entityId: number): { x: number; y: number; width: number; height: number } | null {
    return this.painter.markRect(entityId);
  }

  setPixelRatioCap(cap: number): void {
    this.pixelRatioCap = Number.isFinite(cap) ? Math.max(1, Math.min(2, cap)) : 2;
    this.resize();
  }

  setNight(night: boolean): void {
    this.night = night;
  }

  setWeather(kind: number): void {
    if (kind === 1) {
      if (this.snow === null) this.snow = new Snowfall(this.canvas, this.pixelRatioCap);
      else this.snow.enable();
    } else this.snow?.disable();
  }

  resize(): void {
    // Render at device pixels so text and thin strokes are not blurred on a phone,
    // but cap the ratio: a 3x panel costs nine times the fill for a difference no
    // one sees on a 1-metre grid.
    this.dpr = Math.min(this.pixelRatioCap, globalThis.devicePixelRatio ?? 1);
    const width = this.canvas.clientWidth || 960;
    const height = this.canvas.clientHeight || 540;
    this.canvas.width = Math.round(width * this.dpr);
    this.canvas.height = Math.round(height * this.dpr);
    this.camera.resize(width, height);
    this.snow?.resize(this.canvas, this.pixelRatioCap);
  }

  draw(frame: RenderFrame): void {
    // Safari can change devicePixelRatio when a window moves between displays
    // without dispatching a resize event. Keep the backing buffer and camera
    // viewport in step before painting this frame.
    this.resizeIfNeeded();
    const ctx = this.ctx;
    const cameraTarget = frame.cameraAnchor ?? frame.player;
    this.camera.follow(cameraTarget.xMm, cameraTarget.yMm, frame.dtMs);
    // The ground under wherever the camera ends up looking, not under wherever
    // it started: `camera.update()` builds this frame's projection from it, and
    // a stale height would sink or float the eye whenever the camera crosses a
    // slope.
    this.camera.zMm = this.heightField.at(this.camera.xMm, this.camera.yMm);
    this.camera.update();

    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = COLOURS.void;
    ctx.fillRect(0, 0, this.camera.viewportWidth, this.camera.viewportHeight);

    this.drawTerrain(frame.nowMs);
    // Keep the fallback player-visible too. The entity painter follows, as in
    // the GL path: darkening health bars would make a gameplay fact harder to
    // read, while the world beneath them is what the environment changes.
    if (this.night) {
      ctx.fillStyle = "rgba(34, 53, 101, 0.58)";
      ctx.fillRect(0, 0, this.camera.viewportWidth, this.camera.viewportHeight);
    }
    this.painter.draw(frame);
    if (this.snow !== null && !this.snow.draw(frame.nowMs)) {
      this.snow.destroy();
      this.snow = null;
    }

    ctx.restore();
  }

  private drawTerrain(nowMs: number): void {
    const terrain = this.terrain;
    const map = this.terrainMap;
    if (terrain === null || map === null) return;

    const ctx = this.ctx;
    const scale = map.cellMm / this.camera.mmPerPixel;
    const bounds = this.camera.visibleBounds(map.cellMm, {
      minMm: this.heightField.texture.minMm,
      maxMm: this.heightField.texture.minMm + this.heightField.texture.spanMm,
    });

    // Blit only the cells on screen. Scaling the whole 1024x1280 cache up to the
    // ~43,000 px the world would occupy and letting the browser clip it works, but
    // it hands the compositor a bitmap two orders of magnitude larger than the
    // window on every frame; a phone notices.
    const cellX0 = clamp(Math.floor((bounds.minXMm - map.baseXMm) / map.cellMm), 0, map.worldCellsX);
    const cellX1 = clamp(Math.ceil((bounds.maxXMm - map.baseXMm) / map.cellMm), 0, map.worldCellsX);
    // The cache's rows run north to south, so the *top* row corresponds to the
    // largest world y. Converting through row = worldCells - y keeps that in one
    // place rather than scattering the flip through the arithmetic.
    const rowY0 = clamp(
      Math.floor(map.worldCellsY - (bounds.maxYMm - map.baseYMm) / map.cellMm),
      0,
      map.worldCellsY,
    );
    const rowY1 = clamp(
      Math.ceil(map.worldCellsY - (bounds.minYMm - map.baseYMm) / map.cellMm),
      0,
      map.worldCellsY,
    );
    if (cellX1 <= cellX0 || rowY1 <= rowY0) return;

    // Ground level: this cache has no elevation of its own to offer `project`,
    // and it is the top-left corner of a flat blit either way -- see the note
    // at the top of this file on why the blit itself is not perspective-correct.
    const corner = this.camera.project(
      map.baseXMm + cellX0 * map.cellMm,
      map.baseYMm + (map.worldCellsY - rowY0) * map.cellMm,
      0,
    );
    const destX = corner.x;
    const destY = corner.y;

    // Nearest-neighbour: the grid is authoritative data, and a smoothed edge would
    // draw a wall in a place the collision query says is walkable.
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(
      terrain,
      cellX0,
      rowY0,
      cellX1 - cellX0,
      rowY1 - rowY0,
      destX,
      destY,
      (cellX1 - cellX0) * scale,
      (rowY1 - rowY0) * scale,
    );

    this.drawWaterSurface(map, cellX0, cellX1, rowY0, rowY1, destX, destY, scale, nowMs);

    if (scale >= 8) {
      this.drawGridLines(cellX1 - cellX0, rowY1 - rowY0, scale, destX, destY);
    }
  }

  private drawWaterSurface(
    map: CollisionMap,
    cellX0: number,
    cellX1: number,
    rowY0: number,
    rowY1: number,
    destX: number,
    destY: number,
    scale: number,
    nowMs: number,
  ): void {
    const ctx = this.ctx;
    const timeSeconds = nowMs / 1000;
    ctx.save();
    ctx.globalAlpha = 0.9;
    for (let cellY = 0; cellY < map.worldCellsY; cellY++) {
      const row = map.worldCellsY - 1 - cellY;
      if (row < rowY0 || row >= rowY1) continue;
      for (let cellX = cellX0; cellX < cellX1; cellX++) {
        const flags = map.cells[cellY * map.worldCellsX + cellX];
        if ((flags & FLAG_WATER) === 0) continue;
        const [red, green, blue] = waterSurfaceColour(
          (map.baseXMm + cellX * map.cellMm) / 1000,
          (map.baseYMm + cellY * map.cellMm) / 1000,
          timeSeconds,
        );
        ctx.fillStyle = `rgb(${red}, ${green}, ${blue})`;
        ctx.fillRect(
          destX + (cellX - cellX0) * scale,
          destY + (row - rowY0) * scale,
          scale + 0.5,
          scale + 0.5,
        );
      }
    }
    ctx.restore();
  }

  /** Cell boundaries, once they are big enough to read as a grid rather than noise. */
  private drawGridLines(
    columns: number,
    rows: number,
    scale: number,
    originX: number,
    originY: number,
  ): void {
    const ctx = this.ctx;
    ctx.strokeStyle = COLOURS.grid;
    ctx.lineWidth = 1;
    ctx.beginPath();
    // The half-pixel offset puts the stroke on the pixel boundary instead of
    // straddling two, which is the difference between a hairline and a smear.
    for (let column = 0; column <= columns; column++) {
      const x = Math.round(originX + column * scale) + 0.5;
      ctx.moveTo(x, 0);
      ctx.lineTo(x, this.camera.viewportHeight);
    }
    for (let row = 0; row <= rows; row++) {
      const y = Math.round(originY + row * scale) + 0.5;
      ctx.moveTo(0, y);
      ctx.lineTo(this.camera.viewportWidth, y);
    }
    ctx.stroke();
  }

  drawJoystick(
    origin: { x: number; y: number },
    knob: { x: number; y: number },
    radiusPx: number,
  ): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.painter.drawJoystick(origin, knob, radiusPx);
    ctx.restore();
  }

  private resizeIfNeeded(): void {
    const dpr = Math.min(this.pixelRatioCap, globalThis.devicePixelRatio ?? 1);
    const width = this.canvas.clientWidth || 960;
    const height = this.canvas.clientHeight || 540;
    const pixelWidth = Math.round(width * dpr);
    const pixelHeight = Math.round(height * dpr);
    if (
      this.dpr !== dpr ||
      this.canvas.width !== pixelWidth ||
      this.canvas.height !== pixelHeight
    ) {
      this.resize();
    }
  }
}
